/* eslint-disable @typescript-eslint/no-explicit-any */
import type { PipeEventProcessorDef, PipeInterface } from '@render/composables/usePipe'
import { ERROR_CONTEXTS, HALT_PROCESSOR_CHAIN, PIPE_EVENTS } from '@render/composables/usePipe'
import type { PipeIntegration, PipeIntegrationDef } from '..'
import type { SerializableHead } from '@unhead/vue/types'
import type { OfferGroupDataDto } from '@api/types.gen'
import { watch } from 'vue'
import { useFetch } from '@render/composables/useFetch'
import {
  PAYMENT_EVENTS,
  resolveActiveVariant,
  type OrderCreatedPayload,
} from '@render/composables/usePaymentEvents'
import {
  GEO_DEVICE_CHECK_EU_COUNTRY_PROCESSOR,
  GEO_DEVICE_REFRESH_ON_IP_CHANGE_PROCESSOR,
} from '@render/integrations/geoDevice/geoDevice'
import { setDeviceTimestampOnce, type DeviceTimestamps } from '@render/lib/deviceTimestamps'

export enum OrderStatus {
  DRAFT = 'DRAFT',
  LEAD = 'LEAD',
  VISIT_CHECKOUT = 'VISIT_CHECKOUT',
  INITIATE_CHECKOUT = 'INITIATE_CHECKOUT',
  PURCHASE = 'PURCHASE',
  FAILED_CHECKOUT = 'FAILED_CHECKOUT',
  PLAN_SELECTED = 'PLAN_SELECTED',
  SUBMIT_CC_INPUT = 'SUBMIT_CC_INPUT',
}

export type UpdateOrderResponseDto = {
  id: string
  status?: OrderStatus
  rawData?: {
    [key: string]: unknown
  }
  paymentsApiCustomerId?: string | null
  originIssuerCountryCode?: string | null
  requestIp?: string | null
  email?: string | null
  loginUrl?: string | null
  userId?: string | null
  userToken?: string | null
  testGroup?: string | number | null // BE DTO types it as string; runtime value is numeric
  testUser?: boolean | null
  data?: {
    [key: string]: unknown
  }
}

/**
 * Coerces one price field off an offer variant to a finite number, or null.
 *
 * `apiBasePrice` / `apiTrialPrice` are typed `number` on `OfferConst` but arrive
 * as **strings** (`"13.50"`): they are mapped straight off the payments-API PPO
 * price row, whose `amount` / `trialAmount` are `decimal` columns, and pg returns
 * numeric/decimal as a string to preserve precision. The `*WoDiscount` /
 * `*PerPeriod` fields come from a `jsonb` column instead, so those really are
 * numbers — which is why a single offer carries both shapes and a
 * `typeof x === 'number'` guard rejects exactly the price the visitor is charged.
 * Same coercion `OfferSummary.fmt` and `OfferSelectComponent.formatPrice` do.
 */
const finitePrice = (value: number | string | undefined | null): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * The gift the exit-intent offer carries: how much less the visitor pays up
 * front by taking `og.exit` instead of `og.regular`.
 *
 * Which pair of prices expresses that is decided by what the *regular* offer
 * charges up front, i.e. by `regular.paymentType` — not by the exit offer's own
 * type, which is `recurring_with_trial` in both cases:
 *
 *  - regular is **not** a trial plan (the single-month offers, `recurring`) —
 *    the visitor would be charged the plan price, and the exit offer's own
 *    strikethrough already expresses the saving:
 *    `exit.apiBasePrice − exit.apiTrialPrice` (19 − 9 = 10).
 *  - regular **is** a trial plan (the multi-month offers,
 *    `recurring_with_trial`) — both offers charge a trial period up front, so
 *    the gift is the difference between the two trial prices:
 *    `regular.apiTrialPrice − exit.apiTrialPrice` (49 − 39 = 10).
 *
 * The branch matters because `apiBasePrice` is a *per-period* price while
 * `apiTrialPrice` covers the whole trial period: on a 6-month plan the base /
 * trial pair is 19-per-month against 39-for-6-months, which is not a comparison
 * at all and reported a 0 gift once clamped.
 *
 * Both are API-resolved prices, which is the point of the `…Actual` suffix on
 * the variable this backs: the authored `basePriceDiscountAmount` /
 * `trialPriceDiscountAmount` / `trialPriceWoDiscount` fields are offer
 * configuration and can disagree with the numbers actually rendered.
 *
 * Null when the group has no exit variant or a price the chosen branch needs is
 * missing. Clamped at 0 so an exit offer priced above the regular one reads as
 * no gift rather than a negative one.
 */
const exitIntentGiftAmount = (group: OfferGroupDataDto | undefined | null): number | null => {
  const exit = group?.exit
  if (!exit) return null

  const isTrialRegular = group?.regular?.paymentType === 'recurring_with_trial'

  const was = finitePrice(isTrialRegular ? group?.regular?.apiTrialPrice : exit.apiBasePrice)
  const now = finitePrice(exit.apiTrialPrice)

  if (was === null || now === null) return null

  return Math.max(0, was - now)
}

const usePipeOrders: PipeIntegration = (): PipeIntegrationDef => {
  let config: { apiHost: string }
  let apiHost: string
  let pipe: PipeInterface
  const { error, get, post } = useFetch()

  // Serialize POSTs so the very first event can create the order, save the
  // returned id back to var.global.orderId, and subsequent events arrive as
  // updates. Without serialization two near-simultaneous events would race on
  // the same empty orderId and create two orders.
  let queueTail: Promise<void> = Promise.resolve()
  const enqueue = (fn: () => Promise<unknown>) => {
    queueTail = queueTail
      .then(() => fn())
      .then(() => undefined)
      .catch((e) => pipe.trackError({ error: e, context: ERROR_CONTEXTS.pipeordersQueue }))
    return queueTail
  }

  // Tracks the last seen non-empty email so the watcher can detect a genuine
  // change (e.g. user edits their email mid-funnel) vs. the initial set.
  let prevEmail: string | null = null

  // Public /api/orders rejects emailless writes (the order is anchored on
  // email — Payments-API customer upsert, first-email DRAFT → LEAD, Klaviyo
  // / Facebook downstream events). Gate every entry point on this so we
  // don't queue dead work or POST requests the BE will 400.
  const hasEmail = (): boolean => {
    const email = pipe.getData('var.global.email')
    return typeof email === 'string' && email.trim().length > 0
  }

  // testGroup / testUser are A/B assignment fields stamped server-side on the
  // order (order.appData) and echoed on every order response. Mirror them into
  // global variables so authors can gate content via visibility rules.
  // Null-check (not truthy) so testUser=false and a numeric testGroup are kept;
  // coerce testGroup to a number since the BE DTO types it as a string.
  const applyTestAssignment = (p: PipeInterface, response: UpdateOrderResponseDto): void => {
    if (response.testGroup != null) {
      const group = Number(response.testGroup)
      if (Number.isFinite(group)) p.setData('var.global.testGroup', group)
    }
    if (response.testUser != null) {
      p.setData('var.global.testUser', response.testUser)
    }
  }

  // BIN-lookup issuing country for the card used on the order's completed
  // payment (null pre-purchase — see PublicOrdersService.serializeOrder).
  // Guard on truthy so a not-yet-purchased response doesn't clobber a
  // previously-cached value.
  //
  // Cache-invalidation hook: this value directly feeds geoDevice's
  // determineIsEUCountry, but applyUsersIps' refreshGeoDeviceOnIpChange
  // dispatch only re-checks when the visitor's IP has moved. A same-IP order
  // whose card issuing country differs from the IP-geo country would
  // otherwise never get isEUCountry recomputed, so ask geoDevice to re-check
  // directly whenever this value is (re)written. Fire-and-forget, mirroring
  // the applyUsersIps dispatch below.
  const applyOriginIssuerCountryCode = (
    p: PipeInterface,
    response: UpdateOrderResponseDto,
  ): void => {
    if (!response.originIssuerCountryCode) return
    p.setData('var.global.originIssuerCountryCode', response.originIssuerCountryCode)

    const checkEuCountry = p.getEventProcessor(GEO_DEVICE_CHECK_EU_COUNTRY_PROCESSOR)
    if (checkEuCountry) {
      void Promise.resolve(checkEuCountry.handler({ args: [], data: undefined, pipe: p })).catch(
        (e) => p.trackError({ error: e, context: ERROR_CONTEXTS.eventProcessor }),
      )
    }
  }

  // initial = requestIp from the very first response this pipe session ever
  // saw, kept sticky once set. current = requestIp from the latest response.
  // They diverge when an abandoned-cart link is resumed from a different
  // network — authors can branch on that instead of only ever seeing the
  // latest IP. Entirely frontend-derived (persisted like any var.global.*),
  // not sourced from a BE-persisted "first touch" column.
  const applyUsersIps = (p: PipeInterface, response: UpdateOrderResponseDto): void => {
    if (response.requestIp == null) return
    const existing = p.getData('var.global.usersIPs') as
      | { initial?: string | null; current?: string | null }
      | undefined
    p.setData('var.global.usersIPs', {
      initial: existing?.initial ?? response.requestIp,
      current: response.requestIp,
    })

    // Cache-invalidation hook: usersIPs just got fresher than whatever
    // geoDevice last saw, so ask it to re-check (and re-fetch if the IP
    // actually moved) rather than waiting for an author-wired onPageShow.
    // Looked up dynamically — geoDevice may not be an active integration on
    // every pipe — and fired fire-and-forget (order upsert shouldn't block
    // on a geo lookup), mirroring the registry-dispatch pattern in
    // usePipe.ts's flushPendingComponentDispatches.
    const refreshGeoDevice = p.getEventProcessor(GEO_DEVICE_REFRESH_ON_IP_CHANGE_PROCESSOR)
    if (refreshGeoDevice) {
      void Promise.resolve(refreshGeoDevice.handler({ args: [], data: undefined, pipe: p })).catch(
        (e) => p.trackError({ error: e, context: ERROR_CONTEXTS.eventProcessor }),
      )
    }
  }

  // The backend can change the email it was handed. A known mistyped domain
  // is rewritten on the way in (gmial.com → gmail.com), and the order is then
  // anchored on the corrected address — Payments-API customer, Klaviyo,
  // Facebook, the NNP registration. Mirror it back so the pipe agrees with
  // the order: otherwise the next POST re-sends the typo, and Klaviyo /
  // Segment stay identified against an address nothing will ever mail.
  //
  // Writing var.global.email trips the email watcher in setup(), which emits
  // resetUser — correct here, the identity genuinely moved. The watcher does
  // not POST, so this cannot feed back into another upsert.
  //
  // var.global.emailPreAutoCorrect is what the visitor actually typed, for authors
  // who want to confirm the correction on-screen. It is server-owned (written
  // into rawData by PublicOrdersService.applyEmailSubstitution) — mirrored
  // verbatim, never derived here, and cleared once the backend stops
  // reporting one.
  const applyServerEmail = (p: PipeInterface, response: UpdateOrderResponseDto): void => {
    const preAutoCorrect = response.rawData?.['var.global.emailPreAutoCorrect']
    p.setData(
      'var.global.emailPreAutoCorrect',
      typeof preAutoCorrect === 'string' && preAutoCorrect.trim() ? preAutoCorrect.trim() : null,
    )

    const serverEmail = typeof response.email === 'string' ? response.email.trim() : ''
    if (!serverEmail) return

    const current = p.getData('var.global.email')
    const currentEmail = typeof current === 'string' ? current.trim() : ''
    if (!currentEmail || currentEmail.toLowerCase() === serverEmail.toLowerCase()) return

    console.info('[pipeorders] order email changed by the backend — adopting the returned value')
    p.setData('var.global.email', serverEmail)
  }

  const upsertOrder = async (status?: OrderStatus): Promise<UpdateOrderResponseDto | null> => {
    if (typeof window === 'undefined') return null

    // The user-wired `pipeorders-create-order` processor calls upsertOrder()
    // directly without pre-gating (unlike the wire(...) subscriptions), so
    // await the localStorage merge here too before reading email — same
    // refresh race as the wire(...) handlers.
    await pipe.whenReady()

    // Defense-in-depth — the wire(...) subscriptions already gate on email
    // before enqueueing, but `createOrder` (and its inline retries) call
    // upsertOrder unconditionally and rely on this gate.
    if (!hasEmail()) {
      console.warn(
        `[pipeorders] skipping order upsert (status=${status ?? 'create'}) — var.global.email is not set yet`,
      )
      return null
    }

    // This POST fixes the `_fbp`/`_fbc` that orders/api forwards to the Meta
    // CAPI and NNP, so the snapshot has to be current as of now rather than as
    // of the landing page. The handler runs inline (see
    // PIPE_EVENTS.trackingRefresh) — keep this emit immediately before the
    // serialization below. No listener when TRACKING_PROPERTIES is disabled.
    pipe.emit(PIPE_EVENTS.trackingRefresh)

    // Capture once, before the POST, so it's persisted on the very request
    // that creates the order: the visitor's device-local time at their first
    // order-creation attempt. setDeviceTimestampOnce's own guard covers the
    // "not already captured" case; the orderId check additionally avoids
    // touching it on every later status upsert. Feeds Klaviyo's
    // `leadEmailSubmissionLocalHour` lead-event property — order creation,
    // not leadEmailSubmission, is what actually triggers that event (order
    // create → NNP registration → NNP_2_USER_CREATED → klaviyo-profile-sync),
    // regardless of where/when the email itself was captured on the page.
    if (!pipe.getData('var.global.orderId')) {
      setDeviceTimestampOnce(pipe, 'orderCreation')
    }

    const body: { rawData: Record<string, unknown>; status?: OrderStatus } = {
      rawData: {
        ...Object.fromEntries(
          Object.entries(pipe.variables.value).map(([k, v]) => [`var.${k}`, v]),
        ),
        ...Object.fromEntries(
          Object.entries(pipe.constants.value).map(([k, v]) => [`const.${k}`, v]),
        ),
      },
    }
    if (status) body.status = status
    const creating = !body.rawData['var.global.orderId']

    // 15s deadline: this POST sits INSIDE awaited onClick chains (create-order
    // → navigate), so a hung socket here used to freeze navigation forever —
    // the user stared at a dead button while the click event was already
    // tracked and the next page's view never fired. Bounding it converts a
    // hang into a fast, observable failure the chain can move past.
    const response = (await post(`${apiHost}/api/orders`, { body, timeoutMs: 15_000 })) as
      | UpdateOrderResponseDto
      | null
      | undefined

    // post() rejects on every failure of THIS request (non-2xx, network
    // error, abort), so reaching here means our own POST succeeded — only an
    // empty/null body remains to check. Deliberately do NOT consult the
    // shared `error.value` here: it belongs to the single module-wide
    // useFetch instance, so a concurrent pipeorders request (e.g. an
    // enqueued payment:* upsert) failing while this POST is in flight would
    // false-positive a successful create — wrongly halting the processor
    // chain in createOrder and discarding the returned orderId (→ duplicate
    // order on retry).
    if (!response) {
      throw new Error(`Order ${status ?? 'create'} upsert failed: empty response body`)
    }

    if (!pipe.getData('var.global.orderId')) {
      pipe.setData('var.global.orderId', response.id)
      // The lead. A concurrent create the backend deduped onto this order finds the id
      // already set; an update lands here only after a landing reset wiped it.
      if (creating) {
        const created: OrderCreatedPayload = { orderId: response.id }
        pipe.emit(PAYMENT_EVENTS.orderCreated, created)
      }
    }

    // Refresh paymentsApiCustomerId from every response — the BE can assign
    // or rotate it on UPDATEs (first-touch fallback when the create-time
    // Payments-API upsert failed; or email rotation re-upserting against a
    // different customer record). Guard on truthy so a transient
    // Payments-API outage that returns null doesn't clobber a known-good id.
    if (response.paymentsApiCustomerId) {
      pipe.setData('var.global.paymentsApiCustomerId', response.paymentsApiCustomerId)
    }

    applyServerEmail(pipe, response)

    applyOriginIssuerCountryCode(pipe, response)

    applyUsersIps(pipe, response)

    // Opportunistic loginUrl capture — most orders have appData.oneTimeToken
    // populated by the async-signup processor by the time the user reaches a
    // post-purchase page, so the navigate-to-app processor can usually skip
    // the on-demand `?waitForLoginUrl=true` GET. The BE returns null until a
    // token exists; only overwrite when present so a transient null doesn't
    // clobber a previously-cached URL.
    if (response.loginUrl) {
      pipe.setData('var.global.loginUrl', response.loginUrl)
    }

    applyTestAssignment(pipe, response)

    return response
  }

  // Diagnostic funnel events, GTM-only by design: routed through the gtm_event
  // processor (dataLayer push) instead of PAYMENT_EVENTS.trackingCustomEvent,
  // which Klaviyo and Segment also consume — order-create failures are ops
  // telemetry, not marketing signals. Fire-and-forget; missing integration
  // (gtm disabled on the pipe) degrades to a no-op.
  const emitOrderDiagnostic = (eventName: string, params: Record<string, unknown>) => {
    const gtmEvent = pipe.getEventProcessor('gtm_event')
    if (!gtmEvent) return
    void Promise.resolve(gtmEvent.handler({ args: [], data: { eventName, params }, pipe })).catch(
      (e) => pipe.trackError({ error: e, context: ERROR_CONTEXTS.eventProcessor }),
    )
  }

  // Retry policy for failed order POSTs: only transport-level failures are
  // retried — a timeout, a thrown network error (no HTTP status), or a 5xx.
  // 4xx is the BE refusing the payload (e.g. 422 invalid email); retrying
  // would just repeat the refusal and pollute Bugsnag/diagnostics.
  const MAX_UPSERT_RETRIES = 2
  const UPSERT_RETRY_BACKOFF_MS = [2_000, 5_000]

  const isRetryableOrderError = (e: unknown): boolean => {
    const fe = e as { timedOut?: boolean; status?: number | null } | undefined
    if (fe?.timedOut === true) return true
    const status = fe?.status
    return status == null || status >= 500
  }

  // Background retry scheduler. Each attempt is enqueued individually and the
  // backoff sleeps OUTSIDE the queue (plain setTimeout), so the serialization
  // queue is never held idle between attempts — a click-path createOrder
  // enqueued meanwhile waits only behind real in-flight POSTs (≤10s each),
  // never behind a backoff sleep. The whole chain finishes well inside the
  // BE's 60s create-dedupe window, so attempts land on the same order even
  // when `var.global.orderId` was never written back. If a parallel producer
  // succeeded meanwhile, the retry simply becomes a harmless update.
  // One Bugsnag event per status upsert, sent once it has finally failed: a
  // blip that a retry absorbs is not actionable. Each failed attempt is still
  // on the event as a `fetch() error` request breadcrumb (Bugsnag's own).
  const reportUpsertFailure = (
    e: unknown,
    status: OrderStatus | undefined,
    attempts: number,
    startedAt: number,
  ) =>
    pipe.trackError({
      error: e,
      context: status ? ERROR_CONTEXTS.pipeordersQueue : ERROR_CONTEXTS.pipeordersCreateOrder,
      metadata: { orderStatus: status ?? 'create', attempts, elapsedMs: Date.now() - startedAt },
    })

  const scheduleUpsertRetry = (
    status: OrderStatus | undefined,
    attempt: number,
    startedAt: number,
  ) => {
    if (attempt > MAX_UPSERT_RETRIES) return
    setTimeout(
      () => {
        void enqueue(() =>
          upsertOrder(status).catch((e) => {
            if (isRetryableOrderError(e) && attempt < MAX_UPSERT_RETRIES) {
              scheduleUpsertRetry(status, attempt + 1, startedAt)
              return
            }
            reportUpsertFailure(e, status, attempt + 1, startedAt)
            emitOrderDiagnostic('order_upsert_retries_exhausted', {
              orderStatus: status ?? 'create',
              attempts: attempt + 1,
            })
          }),
        )
      },
      UPSERT_RETRY_BACKOFF_MS[attempt - 1] ?? 5_000,
    )
  }

  // User-wired processor (`pipeorders-create-order`, typically
  // `[pipeorders-create-order, navigate]` on the email-step CTA). Unlike the
  // background status upserts, this is AWAITED by the CTA's onClick chain — so
  // it keeps the user on the email step with the CTA loader spinning while it
  // (re)tries, instead of advancing on a failed/absent order. Retries are done
  // INLINE (awaited backoff) rather than detached via scheduleUpsertRetry so
  // the click path stays pending across the whole sequence. On success it
  // resolves normally and the chain continues to `navigate`; when retries are
  // exhausted it surfaces the NetworkErrorBanner and returns
  // HALT_PROCESSOR_CHAIN so the trailing `navigate` is skipped. The BE dedupes
  // concurrent creates on (email, pipe, day) within 60s, so awaiting here (not
  // going through the shared serialization queue) can't create duplicates.
  const createOrder = async () => {
    const startedAt = Date.now()
    // Total tries = 1 initial + MAX_UPSERT_RETRIES. `attempt` indexes the
    // backoff array for the NEXT sleep (0 → first retry waits BACKOFF[0]).
    let attempt = 0
    for (;;) {
      try {
        await upsertOrder()
        return // created (or no-email skip resolved without throwing) → advance
      } catch (e) {
        // Only transport-level failures (timeout / network / 5xx) are retried;
        // a 4xx is the BE refusing the payload and would just repeat.
        if (isRetryableOrderError(e) && attempt < MAX_UPSERT_RETRIES) {
          await new Promise((resolve) =>
            setTimeout(resolve, UPSERT_RETRY_BACKOFF_MS[attempt] ?? 5_000),
          )
          attempt++
          continue
        }

        // Terminal failure — keep the user on the email step (HALT below).
        const fetchError = e as { timedOut?: boolean; status?: number | null } | undefined
        const timedOut = fetchError?.timedOut === true
        const status = typeof fetchError?.status === 'number' ? fetchError.status : null
        const elapsedMs = Date.now() - startedAt

        pipe.trackError({
          error: e,
          context: ERROR_CONTEXTS.pipeordersCreateOrder,
          metadata: {
            timedOut,
            status,
            elapsedMs,
            attempts: attempt + 1,
            hasOrderId: !!pipe.getData('var.global.orderId'),
          },
        })

        emitOrderDiagnostic(
          timedOut
            ? 'order_create_timeout'
            : status === 422
              ? 'order_create_rejected_422'
              : 'order_create_failed',
          {
            status,
            elapsedMs,
            attempts: attempt + 1,
            hasOrderId: !!pipe.getData('var.global.orderId'),
          },
        )

        if (status === 422) {
          // BE refused to create the order (in practice: a malformed email
          // that slipped past an older bundle's input validation). Not a
          // connectivity problem — expose the state so authors can bind a
          // back-to-email redirect / message; no network banner.
          pipe.setData('var.global.emailRejected', true)
        } else if (isRetryableOrderError(e)) {
          // Exhausted transport-level retries and there's no order to charge:
          // surface the NetworkErrorBanner ("check your connection and
          // refresh") — mounted by the build's app.vue and the editor preview.
          pipe.emit(PIPE_EVENTS.networkError, { source: 'pipeorders-create-order', error: e })
        }

        // Skip the rest of the onClick chain (the `navigate`), so the user
        // stays on the email step.
        return HALT_PROCESSOR_CHAIN
      }
    }
  }

  // User-wired action: navigate the browser to the post-signup NNP login URL.
  // Cache-first → fetch-fallback. The cache is populated by every POST that
  // already returns a token (most orders by the time the user reaches a
  // post-purchase page); the GET with `?waitForLoginUrl=true` is the
  // on-demand failsafe, asking the BE to create the NNP user inline before
  // responding. See orders/api/.../LOGIN_URL_PORT_NOTES.md.
  const navigateToApp = async () => {
    if (typeof window === 'undefined') return

    const cached = pipe.getData('var.global.loginUrl') as string | null | undefined
    if (cached) {
      window.location.assign(cached)
      return
    }

    const orderId = pipe.getData('var.global.orderId') as string | undefined
    if (!orderId) {
      console.warn(
        '[pipeorders] navigate-to-app: var.global.orderId not set; cannot resolve login URL',
      )
      return
    }

    try {
      const response = await get<UpdateOrderResponseDto>(`${apiHost}/api/orders`, {
        params: { orderId, waitForLoginUrl: 'true' },
      })
      if (response?.paymentsApiCustomerId) {
        pipe.setData('var.global.paymentsApiCustomerId', response.paymentsApiCustomerId)
      }
      if (response) applyOriginIssuerCountryCode(pipe, response)
      if (response) applyUsersIps(pipe, response)
      if (response) applyTestAssignment(pipe, response)
      if (response?.loginUrl) {
        pipe.setData('var.global.loginUrl', response.loginUrl)
        window.location.assign(response.loginUrl)
        return
      }
      console.warn(
        '[pipeorders] navigate-to-app: backend returned null loginUrl (no NNP user / token yet)',
      )
    } catch (e) {
      pipe.trackError({
        error: e,
        context: ERROR_CONTEXTS.pipeordersNavigateToApp,
        metadata: { orderId },
      })
      // Same user-facing surface as createOrder — the user clicked a
      // "continue to app" CTA and nothing happened; tell them to retry.
      pipe.emit(PIPE_EVENTS.networkError, { source: 'pipeorders-navigate-to-app', error: e })
    }
  }

  // Ask the backend to synthesize a "completed" NPS webhook against itself.
  // Gated on `const.system.appEnv === 'development'` (baked into the bundle
  // at build time from the codebuild APP_ENV — see builder/nuxt.config.ts +
  // builder/app/app.vue). We cannot rely on `import.meta.env.DEV` because
  // pipes are built bundles (Vite mode = production at build time) even when
  // previewed in dev. The backend's DevToolsModule is also conditionally
  // registered (404s outside dev) and NpsPaymentsDevMockService throws 403
  // as further defence in depth.
  const triggerDevWebhookReplay = async () => {
    if (typeof window === 'undefined') return
    if (pipe.getData('const.system.appEnv') !== 'development') return
    const orderId = pipe.getData('var.global.orderId') as string | undefined
    const paymentId = pipe.getData('var.global.lastPaymentId') as string | undefined
    if (!orderId || !paymentId) {
      console.warn(
        `[pipeorders] dev webhook replay: missing ids; skipping (orderId=${orderId ?? 'null'}, paymentId=${paymentId ?? 'null'})`,
      )
      return
    }
    try {
      await post(`${apiHost}/api/dev/nps-payments/replay-webhook`, {
        body: { originId: orderId, paymentId },
      })
    } catch (e) {
      // Gated above to dev-only builds, so any failure here is a real dev signal.
      pipe.trackError({
        error: e,
        context: ERROR_CONTEXTS.pipeordersDevWebhookReplay,
        metadata: { orderId, paymentId },
      })
    }
  }

  return {
    blocking: true,
    getHead(): SerializableHead {
      return {}
    },

    setup(pipeInstance: PipeInterface, setupConfig: any) {
      // Derive var.global.discountCode as `{firstname}_{shortdate}` (e.g.
      // `john_jul17`) so any field/processor/rule can bind to it. Runs on
      // both SSR and client (the math is SSR-safe — Intl.DateTimeFormat +
      // new Date() work in Node, no DOM or network access). Must stay above
      // the `typeof window` guard below: if it only ran on the client, SSR
      // would render an empty discountCode while the client (at hydration
      // time, before the localStorage→firstName merge) would compute the
      // date-only form, causing a hydration text-content mismatch.
      watch(
        () => [
          pipeInstance.getData('var.global.firstName'),
          pipeInstance.getData('const.system.language'),
        ],
        ([rawName, rawLang]) => {
          const firstPart =
            String(rawName ?? '')
              .trim()
              .split(/[\s_-]+/)[0]
              ?.toLowerCase() ?? ''
          const lang = (rawLang as string) || 'en'
          const now = new Date()
          const monthName = new Intl.DateTimeFormat(lang, { month: 'short' }).format(now)
          const shortDate = new Intl.DateTimeFormat(lang, {
            month: 'short',
            day: 'numeric',
          })
            .formatToParts(now)
            .map((part) =>
              part.type === 'month' && /^\d+$/.test(part.value) && !/\d/.test(monthName)
                ? monthName
                : part.value,
            )
            .join('')
            .replace(/[\s,.]/g, '')
            .toLowerCase()
          pipeInstance.setData(
            'var.global.discountCode',
            firstPart ? `${firstPart}_${shortDate}` : shortDate,
          )
        },
        { immediate: true },
      )

      // AC parity with legacy `isAbandonedCartViewed` (default lived in
      // pipelines/src/cf-e/src/js/dataStore.js:95). Seeded false for every
      // visitor so downstream components can rely on the variable existing;
      // flipped to true in onReady() when an AC link is redeemed. Runs on
      // both SSR and client (same reasoning as the discountCode watch above
      // — keeps SSR-rendered v-if branches consistent with first client
      // paint, before the localStorage→variables merge). The merge in
      // pipe.initialize() runs after setup(), so a persisted `true` from a
      // prior session still wins.
      if (pipeInstance.getData('var.global.isAbandonedCartViewed') === undefined) {
        pipeInstance.setData('var.global.isAbandonedCartViewed', false)
      }

      if (typeof window === 'undefined') return

      config = setupConfig
      apiHost = config.apiHost
      pipe = pipeInstance

      // Derive var.global.checkoutOffer from selectedOfferGroup +
      // hasExitIntentBeenAccepted so authors can bind fields to a single
      // resolved OfferConst instead of branching themselves. Mirrors the read-
      // time `resolveActiveVariant` already used by buildPaymentEventPayload.
      // immediate:true so the localStorage→variables hydration on hard reload
      // flushes through before consumers render.
      watch(
        () => [
          pipe.getData('var.global.selectedOfferGroup'),
          pipe.getData('var.global.hasExitIntentBeenAccepted'),
        ],
        ([offer, isExit]) => {
          const og = offer as OfferGroupDataDto | null | undefined
          if (!og) {
            pipe.setData('var.global.checkoutOffer', null)
            pipe.setData('var.global.checkoutDiscountAmount', null)
            pipe.setData('var.global.exitIntentDiscountAmountActual', null)
            return
          }
          const variant = resolveActiveVariant(og, !!isExit)
          pipe.setData('var.global.checkoutOffer', variant)

          const wo = variant.basePriceWoDiscount
          const cur = variant.apiBasePrice
          const discount =
            typeof wo === 'number' && typeof cur === 'number' ? Math.max(0, wo - cur) : null
          pipe.setData('var.global.checkoutDiscountAmount', discount)

          // The gift the exit-intent offer carries — how much less is charged up
          // front than on `og.regular`. Derived from the offer group directly
          // rather than from the resolved `variant` above, so it is populated
          // *before* the visitor accepts: the exit modal itself usually has to
          // name the amount it is offering, and gating on
          // hasExitIntentBeenAccepted would leave it null at exactly that
          // moment. Null when the offer group has no exit variant configured, or
          // when a price the comparison needs is missing — see
          // `exitIntentGiftAmount`.
          pipe.setData('var.global.exitIntentDiscountAmountActual', exitIntentGiftAmount(og))
        },
        { immediate: true },
      )

      // Once an email is captured, mark the user as having agreed to marketing.
      // One-way: never reset to false if email is later cleared.
      // On a genuine email change (not first set), emit resetUser so Klaviyo
      // and Segment can clear their identity state and re-identify.
      watch(
        () => pipe.getData('var.global.email'),
        (email) => {
          if (typeof email === 'string' && email.trim().length > 0) {
            const trimmed = email.trim()

            // Needed for Klaviyo/Segment cookies and data to be reset, so that we don't allocate data to different user
            if (prevEmail !== null && prevEmail !== trimmed) {
              pipe.emit(PAYMENT_EVENTS.resetUser)
            }

            // Capture once: the visitor's device-local time when email first
            // went from unset to set. setDeviceTimestampOnce guards on
            // presence in var.global.deviceTimestamps (not on prevEmail,
            // which is only in-memory), so a hard reload replaying an
            // already-persisted email doesn't clobber the original
            // submission moment with this session's time. Not currently
            // consumed downstream (Klaviyo's `leadEmailSubmissionLocalHour`
            // reads off `orderCreation` instead, below — see that capture
            // site) — kept for its own sake as the true email-entry moment.
            setDeviceTimestampOnce(pipe, 'leadEmailSubmission')

            prevEmail = trimmed
            pipe.setData('var.global.marketingAgreed', true)
          }
        },
        { immediate: true },
      )

      // payment:* event → OrderStatus mapping. The single source of truth lives
      // in ORDER_STATUS_TRANSITIONS.md (orders/api). usePipe.subscribe replays
      // event history for late subscribers, so events that fire before this
      // setup() hook still reach us. Legacy parity audit:
      //  - planSelected → PLAN_SELECTED  (extends legacy; legacy never set this)
      //  - checkoutVisited → VISIT_CHECKOUT  (legacy commonBasicPlanCheckout.js#L145)
      //  - checkoutRendered → INITIATE_CHECKOUT  (legacy primer.js#L111 + commonUpsell.js#L140)
      //  - checkoutStarted → SUBMIT_CC_INPUT  (legacy primer.js#L188 — onPaymentCreationStart)
      //  - checkoutFailed → FAILED_CHECKOUT  (legacy primer.js#L460)
      //  - checkoutCompleted → no PUBLIC transition. PURCHASE is PRIVATE-only;
      //    the NPS payments webhook is the single authority. See
      //    orders/api/src/modules/nps-payments.
      const wire = (
        event: string,
        status: OrderStatus,
        deviceTimestampKey?: keyof DeviceTimestamps,
      ) =>
        pipe.subscribe(event, async () => {
          // The boot-time localStorage→variables merge runs inside a
          // setTimeout(0), so on a hard refresh an early payment event (e.g.
          // OfferSelectComponent auto-emitting plan-selected for the default
          // offer on mount, or subscribe()'s eventHistory replay) can fire
          // before var.global.email is populated. Await the merge so the gate
          // reads the persisted email instead of a transient empty value.
          await pipe.whenReady()
          if (!hasEmail()) {
            console.warn(
              `[pipeorders] dropping ${event} (status=${status}) — var.global.email is not set yet, not queueing`,
            )
            return
          }
          // Set before enqueueing so it's present in pipe.variables.value by
          // the time the queued upsertOrder builds its rawData body — rides
          // this same status POST, no extra network call.
          if (deviceTimestampKey) setDeviceTimestampOnce(pipe, deviceTimestampKey)
          // Catch inside the queued fn (instead of relying on enqueue's
          // generic catch) so a transport failure can hand the status to the
          // bounded background retry — a lost VISIT_CHECKOUT/PLAN_SELECTED
          // breaks AC emails and status-driven attribution downstream.
          enqueue(() => {
            const startedAt = Date.now()
            return upsertOrder(status).catch((e) => {
              if (isRetryableOrderError(e)) scheduleUpsertRetry(status, 1, startedAt)
              else reportUpsertFailure(e, status, 1, startedAt)
            })
          })
        })

      wire(PAYMENT_EVENTS.planSelected, OrderStatus.PLAN_SELECTED)
      wire(PAYMENT_EVENTS.checkoutVisited, OrderStatus.VISIT_CHECKOUT)
      wire(PAYMENT_EVENTS.checkoutRendered, OrderStatus.INITIATE_CHECKOUT)
      wire(PAYMENT_EVENTS.checkoutStarted, OrderStatus.SUBMIT_CC_INPUT, 'paymentAttempted')
      wire(PAYMENT_EVENTS.checkoutFailed, OrderStatus.FAILED_CHECKOUT)

      // NPS staging cannot deliver webhooks to a developer's machine, so on
      // `payment:checkout-completed` we ask our backend to synthesize and POST
      // a "completed" webhook to itself. Gated at fire time on the build-time
      // `const.system.appEnv` constant (seeded by builder/app/app.vue from
      // the codebuild APP_ENV) so staging / production bundles never even
      // make the network call. Toggle off in dev with
      // `NPS_WEBHOOK_DEV_MOCK_DISABLED=true` on the BE. We cannot gate on
      // `import.meta.env.DEV` because pipes are built bundles (Vite mode =
      // production at build time) even when previewed in dev. See
      // orders/api/src/modules/dev-tools/nps-payments-dev-mock.
      pipe.subscribe(PAYMENT_EVENTS.checkoutCompleted, () => {
        if (pipe.getData('const.system.appEnv') !== 'development') return
        enqueue(() => triggerDevWebhookReplay())
      })
    },

    async onReady(pipeInstance: PipeInterface, setupConfig: { apiHost: string }) {
      if (typeof window === 'undefined') return

      // pipe.whenReady() resolves immediately on this path (loadPersistedData
      // has already run by the time onReady fires — usePipe.ts:772-784), but
      // call it explicitly to be robust to refactors. Required by CLAUDE.md
      // for any code reading var.global.* shortly after mount.

      await pipeInstance.whenReady()

      // Capture once per session: the visitor's device-local time this pipe
      // first loaded ("landing" on the page). Must run AFTER whenReady() —
      // the boot-time localStorage merge (loadPersistedData) does a shallow
      // per-key overwrite of var.global.* (`{...variables.value, ...parsed}`),
      // so a write to var.global.deviceTimestamps from setup() (which runs
      // before that merge) gets wholesale-clobbered the instant the merge
      // finds an existing (older) persisted deviceTimestamps object — this
      // fired every time except on a visitor's very first-ever pipe load.
      setDeviceTimestampOnce(pipeInstance, 'sessionStarted')

      // Source the order id from either surface — backwards-compat with
      // legacy pipe-platform email links which embed it as `#token=<value>`
      // (URL hash fragment, see setAbandonedPageEmailUrl.ts:15-48). The new
      // surface is `?ac=` in the query string. `?ac=` wins if both present
      // (anyone crafting a hybrid link signals the new format).
      //
      // NOTE on legacy-token compatibility: the legacy `Order.token` was a
      // separate random-string column distinct from `Order.id`. The new
      // orders/api has no such column — only `Order.id` (UUIDv7). So a
      // literal old-format link with a legacy random token in `#token=`
      // will 404; only links whose hash value is a new-system UUIDv7 will
      // resolve. Supports a transitional case where legacy email infra is
      // repointed at the new orders/api with the body of the link updated
      // to carry the new id; does NOT make actually-old links work.
      const params = new URLSearchParams(window.location.search)
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const acOrderId = params.get('ac') ?? hashParams.get('token')
      if (!acOrderId) return
      const usedQuery = params.has('ac')
      const usedHash = !usedQuery && hashParams.has('token')

      // Retry the restore GET on transport failures. An AC visitor's entire
      // state (email, orderId, offer, quiz answers) lives in this order —
      // proceeding un-restored means an orderless walk to a checkout that
      // cannot charge, or worse, binding to a stale localStorage order. Two
      // caveats shape the numbers: (1) this runs inside pipeorders' BLOCKING
      // onReady, i.e. every pipe.whenReady() caller waits on it — so each
      // attempt gets a 15s deadline (not the 30s default) and the whole
      // loop is capped at ~48s worst case (3 × 15s + 1s + 2s backoff); (2) 4xx is not retried — a legacy
      // `#token=` link or a pruned order 404s forever. On final failure we
      // fall through to the un-restored session (unchanged behavior), but
      // the `?ac=` marker is only stripped on success, so a manual reload
      // retries the restore from scratch.
      const AC_RESTORE_ATTEMPTS = 3
      const AC_RESTORE_BACKOFF_MS = [1_000, 2_000]
      let response: UpdateOrderResponseDto | null | undefined
      const restoreStartedAt = Date.now()
      for (let attempt = 1; attempt <= AC_RESTORE_ATTEMPTS; attempt++) {
        try {
          response = await get<UpdateOrderResponseDto>(`${setupConfig.apiHost}/api/orders`, {
            params: { orderId: acOrderId },
            timeoutMs: 15_000,
          })
          break
        } catch (e) {
          const fe = e as { timedOut?: boolean; status?: number | null } | undefined
          const retryable =
            fe?.timedOut === true || fe?.status == null || (fe.status as number) >= 500
          // Reported once, on the attempt that ends the loop — same rule as
          // reportUpsertFailure.
          if (!retryable || attempt === AC_RESTORE_ATTEMPTS) {
            pipe.trackError({
              error: e,
              context: ERROR_CONTEXTS.pipeordersAcRestore,
              metadata: {
                acOrderId,
                stage: 'fetch',
                attempts: attempt,
                retryable,
                elapsedMs: Date.now() - restoreStartedAt,
              },
            })
            emitOrderDiagnostic('ac_restore_failed', {
              status: fe?.status ?? null,
              timedOut: fe?.timedOut === true,
              attempts: attempt,
            })
            return
          }
          await new Promise((r) => setTimeout(r, AC_RESTORE_BACKOFF_MS[attempt - 1] ?? 2_000))
        }
      }
      if (error.value || !response) {
        pipe.trackError({
          error: error.value ?? new Error('AC restore: empty response'),
          context: ERROR_CONTEXTS.pipeordersAcRestore,
          metadata: { acOrderId, stage: 'response-check', status: error.value?.status },
        })
        return
      }

      // Restore pipe variables from the order's rawData. Keys in rawData
      // were written by upsertOrder() above in the shape `var.<dotted>` /
      // `const.<dotted>`. Only restore var.* — const.* belongs to the
      // bundle and must not be overwritten from a captured-at-time snapshot.
      const restored = response.rawData ?? {}
      const restoredKeys: string[] = []
      for (const [k, v] of Object.entries(restored)) {
        if (!k.startsWith('var.')) continue
        if (k.startsWith('var.system.')) continue // session-specific — don't overwrite current visit values
        if (k === 'var.global.trackingProperties') continue // re-captured from current URL/cookies by trackingProperties integration
        if (k === 'var.global.orderId') continue // set explicitly below
        if (k === 'var.global.paymentsApiCustomerId') continue // set explicitly below from fresh response
        if (k === 'var.global.isAbandonedCartViewed') continue // forced to true below
        // deviceTimestamps mixes per-order facts (leadEmailSubmission,
        // orderCreation — should restore, they describe this order) with a
        // per-visit one (sessionStarted — must NOT restore, or resuming an
        // AC link would overwrite the current visit's landing time with the
        // abandoned session's). Merged explicitly below instead of blanket-
        // restored here.
        if (k === 'var.global.deviceTimestamps') continue
        // Exit-intent flags — AC restore is a NEW exit-intent flow, so the
        // abandoned order's captured exit-intent state must not bleed in.
        if (k === 'var.global.isExitIntentTriggered') continue // new flow → let exit intent re-trigger
        if (k === 'var.global.hasExitIntentBeenAccepted') continue // new flow; also a stale/cross-pipe value would flip offer-variant resolution (regular↔exit)
        if (k === 'var.global.exitIntentTrigger') continue // cleared below — the abandoned session's mechanic is not this visit's
        pipeInstance.setData(k, v)
        restoredKeys.push(k)
      }

      // Merge the order's own device timestamps back in, keeping this
      // session's sessionStarted (set earlier in this same onReady, right
      // after whenReady(), before this restore ran).
      const restoredDeviceTimestamps = restored['var.global.deviceTimestamps'] as
        | DeviceTimestamps
        | undefined
      if (restoredDeviceTimestamps) {
        const current =
          (pipeInstance.getData('var.global.deviceTimestamps') as DeviceTimestamps | null) ?? {}
        pipeInstance.setData('var.global.deviceTimestamps', {
          ...current,
          leadEmailSubmission:
            restoredDeviceTimestamps.leadEmailSubmission ?? current.leadEmailSubmission ?? null,
          orderCreation: restoredDeviceTimestamps.orderCreation ?? current.orderCreation ?? null,
          paymentAttempted:
            restoredDeviceTimestamps.paymentAttempted ?? current.paymentAttempted ?? null,
          paymentSessionCreated:
            restoredDeviceTimestamps.paymentSessionCreated ?? current.paymentSessionCreated ?? null,
          paymentCompleted:
            restoredDeviceTimestamps.paymentCompleted ?? current.paymentCompleted ?? null,
        })
      }

      // Wire identity. setData on var.* auto-persists once
      // persistedDataLoaded === true (always true inside onReady).
      pipeInstance.setData('var.global.orderId', response.id)
      pipeInstance.setData('var.global.paymentsApiCustomerId', response.paymentsApiCustomerId)
      applyOriginIssuerCountryCode(pipeInstance, response)
      applyUsersIps(pipeInstance, response)
      if (response.loginUrl) {
        pipeInstance.setData('var.global.loginUrl', response.loginUrl)
      }

      // testGroup / testUser are top-level response fields (not in rawData), so
      // the rawData restore loop above misses them — set explicitly so a
      // returning AC user keeps their server-assigned A/B bucket.
      applyTestAssignment(pipeInstance, response)

      // Flip the AC marker. Legacy parity:
      // pipelines/src/cf-e/src/js/components/checkoutPlanSelection.js:208-222
      // — set on first AC view, never reset. Downstream components can read
      // this to suppress exit-intent, hide discount popups, etc. (Not
      // implemented in this change — depends on which components opt in.)
      pipeInstance.setData('var.global.isAbandonedCartViewed', true)

      // An AC link starts a NEW exit-intent flow, so no mechanic has triggered
      // it yet. Cleared rather than left absent because the value persists per
      // distribution and an AC pipe can be re-entered from a link after exit
      // intent already fired there.
      pipeInstance.setData('var.global.exitIntentTrigger', null)

      // Strip the marker from the visible URL only on successful restore.
      // Done LAST so a thrown rejection above doesn't strip prematurely.
      // Preserve every other query param (utm_*) and any unrelated hash
      // content.
      let cleanUrl = window.location.pathname
      if (usedQuery) {
        params.delete('ac')
        cleanUrl += params.toString() ? `?${params.toString()}` : ''
        cleanUrl += window.location.hash
      } else if (usedHash) {
        cleanUrl += window.location.search
        hashParams.delete('token')
        const remaining = hashParams.toString()
        cleanUrl += remaining ? `#${remaining}` : ''
      } else {
        cleanUrl += window.location.search + window.location.hash
      }
      window.history.replaceState(window.history.state, '', cleanUrl)
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getEventProcessors(setupConfig: any): PipeEventProcessorDef[] {
      if (typeof window === 'undefined') return []
      return [
        {
          type: 'pipeorders-create-order',
          handler: createOrder,
        },
        {
          type: 'pipeorders-navigate-to-app',
          handler: navigateToApp,
        },
      ]
    },
  }
}
export default usePipeOrders
